import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';

import type { AdvancedOrderType } from '@/constants/advancedOrder';

export const CheckoutModes = {
  market: 'market',
  advanced: 'advanced',
  crossChain: 'cross-chain',
} as const;

export const CHECKOUT_MODE_PER_QUERY_PARAM = {
  'cross-chain': 'crossChain',
  'advanced': 'advanced',
  /** @deprecated use `advanced` instead, this is only for backwards compatibility */
  'limit': 'advanced',
  'market': 'market',
} as const;

export type CheckoutMode = (typeof CheckoutModes)[keyof typeof CheckoutModes];
export type CheckoutModeQueryParam = keyof typeof CHECKOUT_MODE_PER_QUERY_PARAM;

const DEFAULT_CHECKOUT_MODE = CheckoutModes.market;
const LEGACY_LIMIT_QUERY_PARAM = 'limit';

type UseCheckoutModeState = [CheckoutMode, (newCheckoutMode: CheckoutMode) => void];

export function parseCheckoutMode(queryParam: unknown): CheckoutMode {
  if (typeof queryParam !== 'string') return DEFAULT_CHECKOUT_MODE;
  if (!(queryParam in CHECKOUT_MODE_PER_QUERY_PARAM)) return DEFAULT_CHECKOUT_MODE;
  return CheckoutModes[CHECKOUT_MODE_PER_QUERY_PARAM[queryParam as CheckoutModeQueryParam]];
}

export function isAdvancedCheckoutQuery(queryParam: unknown): boolean {
  return queryParam === CheckoutModes.advanced || queryParam === LEGACY_LIMIT_QUERY_PARAM;
}

/**
 * Hook to get and set the checkout mode.
 */
export function useCheckoutMode(): UseCheckoutModeState {
  const router = useRouter();

  const {
    query: { mode: checkoutModeQueryParameter },
  } = router;

  const checkoutMode = parseCheckoutMode(checkoutModeQueryParameter);

  useEffect(() => {
    if (router.query.mode !== LEGACY_LIMIT_QUERY_PARAM) return;

    const query = { ...router.query, mode: CheckoutModes.advanced };
    router.replace({ pathname: router.pathname, query }, undefined, { shallow: true });
  }, [router]);

  const setCheckoutMode = useCallback(
    (newCheckoutMode: CheckoutMode) => {
      const query: {
        mode?: CheckoutMode;
        orderType?: AdvancedOrderType | string | string[];
      } = {
        ...router.query,
        mode: newCheckoutMode,
      };

      if (newCheckoutMode === DEFAULT_CHECKOUT_MODE) delete query.mode;
      if (newCheckoutMode !== CheckoutModes.advanced) delete query.orderType;

      router.push(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );

  return [checkoutMode, setCheckoutMode];
}
